import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_URL } from './config';
import { Link } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine } from 'recharts';
import './Home.css';

function Home() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [equityTimeRange, setEquityTimeRange] = useState('month'); // week, month, ytd, last_year, all
  const [equityAccount, setEquityAccount] = useState('all'); // 'all' or account ID
  const [equityHistory, setEquityHistory] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [dashboardData, setDashboardData] = useState({
    totalEquity: null,
    stockValue: null,
    optionsValue: null,
    cashValue: null,
    optionTypes: {
      longCalls: { count: 0, value: 0 },
      shortCalls: { count: 0, value: 0 },
      longPuts: { count: 0, value: 0 },
      shortPuts: { count: 0, value: 0 }
    },
    totalPositions: 0,
    totalAccounts: 0,
    unrealizedPnL: 0,
    unrealizedPnLBreakdown: {
      total: 0,
      lastTradingDay: 0,
      thisWeek: 0,
      thisMonth: 0,
      yearToDate: 0
    },
    shortPutExposure: 0,
    accountBreakdown: [],
    lastUpdate: null,
    dailyPnL: [],
    weeklyPnL: [],
    monthlyPnL: [],
    realizedPnL: {
      total: 0,
      lastTradingDay: 0,
      thisWeek: 0,
      thisMonth: 0,
      yearToDate: 0,
      allTime: 0
    }
  });

  // P&L Goals (will be user configurable later)
  const pnlGoals = {
    daily: 25,
    weekly: 125,
    monthly: 500
  };

  useEffect(() => {
    fetchDashboardData();
    fetchEquityHistory();
  }, []);

  useEffect(() => {
    fetchEquityHistory();
  }, [equityTimeRange, equityAccount]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch all data in parallel
      const [equityResponse, positionsResponse, accountsResponse, tradesResponse] = await Promise.all([
        axios.get(`${API_URL}/portfolio/equity/`),
        axios.get(`${API_URL}/portfolio/open-positions/?page_size=all`),
        axios.get(`${API_URL}/portfolio/accounts/?page_size=all`),
        axios.get(`${API_URL}/portfolio/trades/?page_size=all`)
      ]);

      // Store accounts for equity graph filter
      const accountsData = accountsResponse.data.results || accountsResponse.data;
      setAccounts(Array.isArray(accountsData) ? accountsData : []);

      // Process equity data (get most recent)
      const equityData = equityResponse.data.results || equityResponse.data;
      const equityArray = Array.isArray(equityData) ? equityData : [];
      
      // Sort by date (newest first) - IBKR format: DD/MM/YY
      const sortedEquity = equityArray.sort((a, b) => {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const parts = dateStr.split('/');
          if (parts.length === 3) {
            const day = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10);
            const year = 2000 + parseInt(parts[2], 10);
            return new Date(year, month - 1, day).getTime();
          }
          return 0;
        };
        return parseDate(b.report_date) - parseDate(a.report_date);
      });

      const latestEquity = sortedEquity[0] || {};

      // Process positions
      const positionsData = positionsResponse.data.results || positionsResponse.data;
      const positionsArray = Array.isArray(positionsData) ? positionsData : [];

      // Filter out synthetic positions (they are aggregates)
      const realPositions = positionsArray.filter(p => 
        (p.asset_category || '').toUpperCase() !== 'SYNTHETIC'
      );

      // Count option types
      const optionTypes = {
        longCalls: { count: 0, value: 0 },
        shortCalls: { count: 0, value: 0 },
        longPuts: { count: 0, value: 0 },
        shortPuts: { count: 0, value: 0 }
      };

      // Calculate exposure for short options
      const calculateShortOptionExposure = (position) => {
        const strike = parseFloat(position.strike || 0);
        const positionValue = parseFloat(position.position || 0); // negative for short
        const multiplier = parseFloat(position.multiplier || 100); // default to 100 for standard options
        const contracts = Math.abs(positionValue); // number of contracts (absolute value)
        
        // Exposure = strike price * multiplier * number of contracts
        return strike * multiplier * contracts;
      };

      let totalUnrealizedPnL = 0;
      let totalShortPutExposure = 0;

      // Account breakdown: count positions per account
      const accountPositionsMap = {};

      // Map positions by conid for matching with trades
      const positionsByConid = {};
      realPositions.forEach(position => {
        const conid = position.conid;
        if (conid) {
          if (!positionsByConid[conid]) {
            positionsByConid[conid] = [];
          }
          positionsByConid[conid].push(position);
        }
      });

      realPositions.forEach(position => {
        const assetCategory = (position.asset_category || '').toUpperCase();
        const putCall = (position.put_call || '').toUpperCase();
        const posValue = parseFloat(position.position || 0);
        const positionValue = parseFloat(position.position_value || 0);
        const unrealizedPnL = parseFloat(position.fifo_pnl_unrealized || 0);
        const accountId = position.account_id || 'Unknown';

        totalUnrealizedPnL += unrealizedPnL;

        // Count positions per account
        if (!accountPositionsMap[accountId]) {
          accountPositionsMap[accountId] = 0;
        }
        accountPositionsMap[accountId] += 1;

        // Only count options
        if (assetCategory === 'OPT' && putCall) {
          if (putCall === 'C') {
            if (posValue > 0) {
              optionTypes.longCalls.count += 1;
              optionTypes.longCalls.value += Math.abs(positionValue);
            } else if (posValue < 0) {
              optionTypes.shortCalls.count += 1;
              optionTypes.shortCalls.value += Math.abs(positionValue);
            }
          } else if (putCall === 'P') {
            if (posValue > 0) {
              optionTypes.longPuts.count += 1;
              optionTypes.longPuts.value += Math.abs(positionValue);
            } else if (posValue < 0) {
              optionTypes.shortPuts.count += 1;
              optionTypes.shortPuts.value += Math.abs(positionValue);
              // Calculate exposure for short puts
              totalShortPutExposure += calculateShortOptionExposure(position);
            }
          }
        }
      });

      // Process accounts first (needed for account breakdown)
      // accountsData already declared above, reuse it
      const accountsArray = Array.isArray(accountsData) ? accountsData : [];

      // Convert account breakdown to array and sort by position count
      const accountBreakdown = Object.entries(accountPositionsMap)
        .map(([accountId, count]) => ({
          accountId,
          count,
          displayName: accountsArray.find(acc => acc.broker_account_id === accountId)?.display_name || accountId
        }))
        .sort((a, b) => b.count - a.count);

      // Get last update date from most recent equity record
      const lastUpdate = latestEquity.report_date || null;

      // Process trades for P&L calculations (only closed positions)
      const tradesData = tradesResponse.data.results || tradesResponse.data;
      const tradesArray = Array.isArray(tradesData) ? tradesData : [];
      
      // Filter only closed positions
      const closedTrades = tradesArray.filter(trade => {
        const openClose = (trade.open_close_indicator || '').toUpperCase();
        return openClose === 'C' || openClose === 'CLOSE';
      });

      // Parse date helper (IBKR format: DD/MM/YY or YYYY-MM-DD)
      const parseTradeDate = (dateStr) => {
        if (!dateStr) return null;
        // Try DD/MM/YY format first
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = 2000 + parseInt(parts[2], 10);
          return new Date(year, month - 1, day);
        }
        // Try YYYY-MM-DD format
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
        return null;
      };

      // Calculate daily P&L
      const dailyPnLMap = {};
      closedTrades.forEach(trade => {
        const tradeDate = parseTradeDate(trade.trade_date);
        if (!tradeDate) return;
        
        const dateKey = tradeDate.toISOString().split('T')[0]; // YYYY-MM-DD
        const pnl = parseFloat(trade.fifo_pnl_realized || 0);
        
        if (!dailyPnLMap[dateKey]) {
          dailyPnLMap[dateKey] = 0;
        }
        dailyPnLMap[dateKey] += pnl;
      });

      // Convert to array and sort by date
      const dailyPnL = Object.entries(dailyPnLMap)
        .map(([date, pnl]) => ({
          date: date.split('-').reverse().join('/').substring(0, 5), // DD/MM format for display
          fullDate: date,
          pnl: parseFloat(pnl.toFixed(2)),
          goal: pnlGoals.daily
        }))
        .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate))
        .slice(-30); // Last 30 days

      // Calculate weekly P&L
      const weeklyPnLMap = {};
      closedTrades.forEach(trade => {
        const tradeDate = parseTradeDate(trade.trade_date);
        if (!tradeDate) return;
        
        // Get start of week (Monday)
        const dayOfWeek = tradeDate.getDay();
        const diff = tradeDate.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
        const weekStart = new Date(tradeDate);
        weekStart.setDate(diff);
        weekStart.setHours(0, 0, 0, 0);
        
        const weekKey = weekStart.toISOString().split('T')[0];
        const pnl = parseFloat(trade.fifo_pnl_realized || 0);
        
        if (!weeklyPnLMap[weekKey]) {
          weeklyPnLMap[weekKey] = 0;
        }
        weeklyPnLMap[weekKey] += pnl;
      });

      const weeklyPnL = Object.entries(weeklyPnLMap)
        .map(([date, pnl]) => {
          const weekDate = new Date(date);
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return {
            date: `${weekDate.getDate()} ${monthNames[weekDate.getMonth()]}`,
            fullDate: date,
            pnl: parseFloat(pnl.toFixed(2)),
            goal: pnlGoals.weekly
          };
        })
        .sort((a, b) => new Date(a.fullDate) - new Date(b.fullDate))
        .slice(-12); // Last 12 weeks

      // Calculate monthly P&L
      const monthlyPnLMap = {};
      closedTrades.forEach(trade => {
        const tradeDate = parseTradeDate(trade.trade_date);
        if (!tradeDate) return;
        
        const monthKey = `${tradeDate.getFullYear()}-${String(tradeDate.getMonth() + 1).padStart(2, '0')}`;
        const pnl = parseFloat(trade.fifo_pnl_realized || 0);
        
        if (!monthlyPnLMap[monthKey]) {
          monthlyPnLMap[monthKey] = 0;
        }
        monthlyPnLMap[monthKey] += pnl;
      });

      const monthlyPnL = Object.entries(monthlyPnLMap)
        .map(([date, pnl]) => {
          const [year, month] = date.split('-');
          const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
          return {
            date: `${monthNames[parseInt(month) - 1]} ${year}`,
            fullDate: date,
            pnl: parseFloat(pnl.toFixed(2)),
            goal: pnlGoals.monthly
          };
        })
        .sort((a, b) => a.fullDate.localeCompare(b.fullDate))
        .slice(-12); // Last 12 months

      // Calculate unrealized P&L breakdown by when positions were opened
      // We need to match open positions with their opening trades
      let lastTradingDayUnrealizedPnL = 0;
      let thisWeekUnrealizedPnL = 0;
      let thisMonthUnrealizedPnL = 0;
      let yearToDateUnrealizedPnL = 0;

      const now = new Date();
      
      // Calculate last trading day (yesterday, but skip weekends)
      // If today is Monday, last trading day is Friday (3 days ago)
      // If today is Sunday, last trading day is Friday (2 days ago)
      // If today is Saturday, last trading day is Friday (1 day ago)
      // Otherwise, last trading day is yesterday
      const dayOfWeek = now.getDay();
      let daysToSubtract = 1;
      if (dayOfWeek === 0) { // Sunday
        daysToSubtract = 2; // Friday
      } else if (dayOfWeek === 1) { // Monday
        daysToSubtract = 3; // Friday
      } else if (dayOfWeek === 6) { // Saturday
        daysToSubtract = 1; // Friday
      }
      
      const lastTradingDay = new Date(now);
      lastTradingDay.setDate(now.getDate() - daysToSubtract);
      lastTradingDay.setHours(0, 0, 0, 0);
      
      // Get start of week (Monday)
      const diff = now.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
      const weekStart = new Date(now);
      weekStart.setDate(diff);
      weekStart.setHours(0, 0, 0, 0);
      
      // Get start of month
      const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
      
      // Get start of year (January 1st)
      const yearStart = new Date(now.getFullYear(), 0, 1);

      // Find opening trades for each open position
      // Opening trades have open_close_indicator = "O" or "OPEN"
      const openingTrades = tradesArray.filter(trade => {
        const openClose = (trade.open_close_indicator || '').toUpperCase();
        return openClose === 'O' || openClose === 'OPEN';
      });

      // Create a map of conid -> earliest opening trade date
      const conidOpeningDates = {};
      openingTrades.forEach(trade => {
        const conid = trade.conid;
        if (!conid) return;
        
        const tradeDate = parseTradeDate(trade.trade_date);
        if (!tradeDate) return;

        if (!conidOpeningDates[conid] || tradeDate < conidOpeningDates[conid]) {
          conidOpeningDates[conid] = tradeDate;
        }
      });

      // Calculate unrealized P&L breakdown by matching positions with their opening dates
      realPositions.forEach(position => {
        const conid = position.conid;
        if (!conid) return;

        const openingDate = conidOpeningDates[conid];
        if (!openingDate) return; // Can't determine when position was opened

        const unrealizedPnL = parseFloat(position.fifo_pnl_unrealized || 0);
        const openingDateOnly = new Date(openingDate.getFullYear(), openingDate.getMonth(), openingDate.getDate());

        // Last trading day
        if (openingDateOnly.getTime() === lastTradingDay.getTime()) {
          lastTradingDayUnrealizedPnL += unrealizedPnL;
        }

        // This week
        if (openingDate >= weekStart) {
          thisWeekUnrealizedPnL += unrealizedPnL;
        }

        // This month
        if (openingDate >= monthStart) {
          thisMonthUnrealizedPnL += unrealizedPnL;
        }

        // Year to date
        if (openingDate >= yearStart) {
          yearToDateUnrealizedPnL += unrealizedPnL;
        }
      });

      // Calculate total realized P&L and period breakdowns
      let totalRealizedPnL = 0;
      let lastTradingDayRealizedPnL = 0;
      let thisWeekRealizedPnL = 0;
      let thisMonthRealizedPnL = 0;
      let yearToDateRealizedPnL = 0;

      closedTrades.forEach(trade => {
        const tradeDate = parseTradeDate(trade.trade_date);
        if (!tradeDate) return;
        
        const pnl = parseFloat(trade.fifo_pnl_realized || 0);
        totalRealizedPnL += pnl;

        // Last trading day (yesterday, accounting for weekends)
        const tradeDateOnly = new Date(tradeDate.getFullYear(), tradeDate.getMonth(), tradeDate.getDate());
        if (tradeDateOnly.getTime() === lastTradingDay.getTime()) {
          lastTradingDayRealizedPnL += pnl;
        }

        // This week
        if (tradeDate >= weekStart) {
          thisWeekRealizedPnL += pnl;
        }

        // This month
        if (tradeDate >= monthStart) {
          thisMonthRealizedPnL += pnl;
        }

        // Year to date
        if (tradeDate >= yearStart) {
          yearToDateRealizedPnL += pnl;
        }
      });

      setDashboardData({
        totalEquity: parseFloat(latestEquity.total || 0),
        stockValue: parseFloat(latestEquity.stock || 0),
        optionsValue: parseFloat(latestEquity.options || 0),
        cashValue: parseFloat(latestEquity.cash || 0),
        optionTypes,
        totalPositions: realPositions.length,
        totalAccounts: accountsArray.length,
        unrealizedPnL: totalUnrealizedPnL,
        unrealizedPnLBreakdown: {
          total: parseFloat(totalUnrealizedPnL.toFixed(2)),
          lastTradingDay: parseFloat(lastTradingDayUnrealizedPnL.toFixed(2)),
          thisWeek: parseFloat(thisWeekUnrealizedPnL.toFixed(2)),
          thisMonth: parseFloat(thisMonthUnrealizedPnL.toFixed(2)),
          yearToDate: parseFloat(yearToDateUnrealizedPnL.toFixed(2))
        },
        shortPutExposure: totalShortPutExposure,
        accountBreakdown,
        lastUpdate,
        dailyPnL,
        weeklyPnL,
        monthlyPnL,
        realizedPnL: {
          total: parseFloat(totalRealizedPnL.toFixed(2)),
          lastTradingDay: parseFloat(lastTradingDayRealizedPnL.toFixed(2)),
          thisWeek: parseFloat(thisWeekRealizedPnL.toFixed(2)),
          thisMonth: parseFloat(thisMonthRealizedPnL.toFixed(2)),
          yearToDate: parseFloat(yearToDateRealizedPnL.toFixed(2)),
          allTime: parseFloat(totalRealizedPnL.toFixed(2))
        }
      });
    } catch (err) {
      if (err.response && err.response.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError('Error fetching dashboard data: ' + err.message);
      }
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEquityHistory = async () => {
    try {
      // Build API URL with filters
      let url = `${API_URL}/portfolio/equity/`;
      if (equityAccount !== 'all') {
        url += `?account_id=${equityAccount}`;
      }

      const response = await axios.get(url);
      const equityData = response.data.results || response.data;
      const equityArray = Array.isArray(equityData) ? equityData : [];

      // Parse dates and filter by time range
      const parseDate = (dateStr) => {
        if (!dateStr) return null;
        // Try DD/MM/YY format (IBKR format)
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const day = parseInt(parts[0], 10);
          const month = parseInt(parts[1], 10);
          const year = 2000 + parseInt(parts[2], 10);
          return new Date(year, month - 1, day);
        }
        // Try YYYY-MM-DD format
        const date = new Date(dateStr);
        if (!isNaN(date.getTime())) {
          return date;
        }
        return null;
      };

      const now = new Date();
      let cutoffDate = null;

      switch (equityTimeRange) {
        case 'week':
          cutoffDate = new Date(now);
          cutoffDate.setDate(now.getDate() - 7);
          break;
        case 'month':
          cutoffDate = new Date(now);
          cutoffDate.setMonth(now.getMonth() - 1);
          break;
        case 'ytd':
          cutoffDate = new Date(now.getFullYear(), 0, 1); // January 1st of current year
          break;
        case 'last_year':
          cutoffDate = new Date(now);
          cutoffDate.setFullYear(now.getFullYear() - 1);
          break;
        case 'all':
        default:
          cutoffDate = null; // No filter
          break;
      }

      // Filter and format data for chart
      const filteredData = equityArray
        .map(item => {
          const date = parseDate(item.report_date);
          if (!date) return null;
          
          if (cutoffDate && date < cutoffDate) return null;

          return {
            date: date.toISOString().split('T')[0], // YYYY-MM-DD for sorting
            dateDisplay: item.report_date, // Keep original format for display
            equity: parseFloat(item.total || 0)
          };
        })
        .filter(item => item !== null)
        .sort((a, b) => new Date(a.date) - new Date(b.date)); // Sort by date ascending

      setEquityHistory(filteredData);
    } catch (err) {
      console.error('Error fetching equity history:', err);
      setEquityHistory([]);
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '€ 0.00';
    }
    const num = parseFloat(value);
    const formattedNumber = num.toLocaleString('nl-NL', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    return `€ ${formattedNumber}`;
  };

  const formatCurrencyWhole = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '€ 0';
    }
    const num = Math.round(parseFloat(value));
    const formattedNumber = num.toLocaleString('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
    return `€ ${formattedNumber}`;
  };

  const formatNumberWhole = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }
    const num = Math.round(parseFloat(value));
    return num.toLocaleString('nl-NL', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    // IBKR format: DD/MM/YY
    const parts = dateStr.split('/');
    if (parts.length === 3) {
      return dateStr; // Return as is for now
    }
    return dateStr;
  };

  if (loading) {
    return (
      <div className="home-container">
        <div className="loading">Loading dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="home-container">
        <div className="error">{error}</div>
        <button onClick={fetchDashboardData} className="refresh-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="home-container">
      <div className="dashboard-header">
        <h1>Portfolio Dashboard</h1>
        {dashboardData.lastUpdate && (
          <p className="last-update">Last update: {formatDate(dashboardData.lastUpdate)}</p>
        )}
      </div>

      {/* Top Row: Total Equity, Realized P&L, Unrealized P&L */}
      <div className="dashboard-top-row" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Main Equity Card */}
        <div className="dashboard-card main-card">
          <div className="card-header">
            <h2>Total Equity</h2>
            <Link to="/equity" className="card-link">View Details →</Link>
          </div>
          <div className="card-value main-value">
            {formatCurrency(dashboardData.totalEquity)}
          </div>
          <div className="card-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">Stocks</span>
              <span className="breakdown-value">{formatCurrency(dashboardData.stockValue)}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Options</span>
              <span className="breakdown-value">{formatCurrency(dashboardData.optionsValue)}</span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Cash</span>
              <span className="breakdown-value">{formatCurrency(dashboardData.cashValue)}</span>
            </div>
          </div>
        </div>

        {/* Realized P&L Card */}
        <div className={`dashboard-card ${dashboardData.realizedPnL.total >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-header">
            <h2>Realized P&L</h2>
            <Link to="/trades" className="card-link">View Trades →</Link>
          </div>
          <div className={`card-value pnl-value ${dashboardData.realizedPnL.total >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(dashboardData.realizedPnL.total)}
          </div>
          <div className="realized-pnl-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">Last Trading Day</span>
              <span className={`breakdown-value ${dashboardData.realizedPnL.lastTradingDay >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.realizedPnL.lastTradingDay)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">This Week</span>
              <span className={`breakdown-value ${dashboardData.realizedPnL.thisWeek >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.realizedPnL.thisWeek)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">This Month</span>
              <span className={`breakdown-value ${dashboardData.realizedPnL.thisMonth >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.realizedPnL.thisMonth)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Year to Date</span>
              <span className={`breakdown-value ${dashboardData.realizedPnL.yearToDate >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.realizedPnL.yearToDate)}
              </span>
            </div>
          </div>
        </div>

        {/* Unrealized P&L Card */}
        <div className={`dashboard-card ${dashboardData.unrealizedPnL >= 0 ? 'positive' : 'negative'}`}>
          <div className="card-header">
            <h2>Unrealized P&L</h2>
            <Link to="/open-positions" className="card-link">View Positions →</Link>
          </div>
          <div className={`card-value pnl-value ${dashboardData.unrealizedPnL >= 0 ? 'positive' : 'negative'}`}>
            {formatCurrency(dashboardData.unrealizedPnL)}
          </div>
          <div className="realized-pnl-breakdown">
            <div className="breakdown-item">
              <span className="breakdown-label">Last Trading Day</span>
              <span className={`breakdown-value ${dashboardData.unrealizedPnLBreakdown.lastTradingDay >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.unrealizedPnLBreakdown.lastTradingDay)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">This Week</span>
              <span className={`breakdown-value ${dashboardData.unrealizedPnLBreakdown.thisWeek >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.unrealizedPnLBreakdown.thisWeek)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">This Month</span>
              <span className={`breakdown-value ${dashboardData.unrealizedPnLBreakdown.thisMonth >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.unrealizedPnLBreakdown.thisMonth)}
              </span>
            </div>
            <div className="breakdown-item">
              <span className="breakdown-label">Year to Date</span>
              <span className={`breakdown-value ${dashboardData.unrealizedPnLBreakdown.yearToDate >= 0 ? 'positive' : 'negative'}`}>
                {formatCurrency(dashboardData.unrealizedPnLBreakdown.yearToDate)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Equity Graph - Full Width */}
      {equityHistory.length > 0 && (
        <div className="dashboard-card chart-card equity-graph-full-width" style={{ marginBottom: '1.5rem' }}>
          <div className="card-header">
            <h2>Equity History</h2>
          </div>
          <div className="chart-filters" style={{ marginBottom: '15px', display: 'flex', gap: '15px', alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="equity-time-range" style={{ fontWeight: '500' }}>Time Range:</label>
              <select
                id="equity-time-range"
                value={equityTimeRange}
                onChange={(e) => setEquityTimeRange(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="ytd">Year to Date</option>
                <option value="last_year">Last Year</option>
                <option value="all">All</option>
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor="equity-account" style={{ fontWeight: '500' }}>Account:</label>
              <select
                id="equity-account"
                value={equityAccount}
                onChange={(e) => setEquityAccount(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '4px', border: '1px solid #ddd' }}
              >
                <option value="all">All Accounts</option>
                {accounts.map(account => (
                  <option key={account.id} value={account.broker_account_id}>
                    {account.display_name || account.broker_account_id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={equityHistory} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="dateDisplay" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                />
                <YAxis 
                  domain={(() => {
                    if (equityHistory.length === 0) return ['auto', 'auto'];
                    const values = equityHistory.map(item => item.equity).filter(val => !isNaN(val) && val !== null);
                    if (values.length === 0) return ['auto', 'auto'];
                    const min = Math.min(...values);
                    const max = Math.max(...values);
                    return [min - 1000, max + 1000];
                  })()}
                  tickFormatter={(value) => formatNumberWhole(value)}
                />
                <Tooltip
                  formatter={(value) => {
                    const acct = accounts.find(a => a.broker_account_id === equityAccount);
                    const sym = acct?.broker === 'tastytrade' ? '$' : '€';
                    const num = parseFloat(value);
                    return `${sym} ${num.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                  }}
                  labelFormatter={(label) => `Date: ${label}`}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="equity" 
                  stroke="#2196F3" 
                  strokeWidth={2} 
                  name="Equity"
                  dot={{ r: 3 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="dashboard-grid">

        {/* Option Types Grid */}
        <div className="dashboard-card option-types-card">
          <div className="card-header">
            <h2>Option Positions</h2>
            <Link to="/open-positions" className="card-link">View All →</Link>
          </div>
          <div className="option-types-grid">
            <div className="option-type-item long-call">
              <div className="option-type-label">Long Calls</div>
              <div className="option-type-count">{dashboardData.optionTypes.longCalls.count}</div>
              <div className="option-type-value">{formatCurrency(dashboardData.optionTypes.longCalls.value)}</div>
            </div>
            <div className="option-type-item short-call">
              <div className="option-type-label">Short Calls</div>
              <div className="option-type-count">{dashboardData.optionTypes.shortCalls.count}</div>
              <div className="option-type-value">{formatCurrency(dashboardData.optionTypes.shortCalls.value)}</div>
            </div>
            <div className="option-type-item long-put">
              <div className="option-type-label">Long Puts</div>
              <div className="option-type-count">{dashboardData.optionTypes.longPuts.count}</div>
              <div className="option-type-value">{formatCurrency(dashboardData.optionTypes.longPuts.value)}</div>
            </div>
            <div className="option-type-item short-put">
              <div className="option-type-label">Short Puts</div>
              <div className="option-type-count">{dashboardData.optionTypes.shortPuts.count}</div>
              <div className="option-type-value">{formatCurrency(dashboardData.optionTypes.shortPuts.value)}</div>
            </div>
          </div>
        </div>

        {/* Short Put Exposure Card */}
        <div className="dashboard-card exposure-card">
          <div className="card-header">
            <h2>Short Put Exposure</h2>
            <Link to="/open-positions" className="card-link">View Details →</Link>
          </div>
          <div className="card-value exposure-value">
            {formatCurrency(dashboardData.shortPutExposure)}
          </div>
          <div className="exposure-note">
            Total capital required if all short puts are assigned
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="dashboard-card stats-card">
          <div className="card-header">
            <h2>Portfolio Statistics</h2>
          </div>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-label">Total Positions</div>
              <div className="stat-value">{dashboardData.totalPositions}</div>
            </div>
            <div className="stat-item">
              <div className="stat-label">Accounts</div>
              <div className="stat-value">{dashboardData.totalAccounts}</div>
            </div>
          </div>
        </div>

        {/* Account Breakdown Card */}
        {dashboardData.accountBreakdown.length > 0 && (
          <div className="dashboard-card account-breakdown-card">
            <div className="card-header">
              <h2>Positions by Account</h2>
              <Link to="/open-positions" className="card-link">View All →</Link>
            </div>
            <div className="account-breakdown-list">
              {dashboardData.accountBreakdown.map((account, idx) => (
                <div key={account.accountId || idx} className="account-breakdown-item">
                  <div className="account-breakdown-name">{account.displayName}</div>
                  <div className="account-breakdown-count">{account.count} positions</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Portfolio Breakdown Pie Chart */}
        {dashboardData.totalEquity > 0 && (
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h2>Portfolio Breakdown</h2>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Stocks', value: Math.abs(dashboardData.stockValue) },
                      { name: 'Options', value: Math.abs(dashboardData.optionsValue) },
                      { name: 'Cash', value: Math.abs(dashboardData.cashValue) }
                    ]}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    <Cell fill="#667eea" />
                    <Cell fill="#764ba2" />
                    <Cell fill="#4caf50" />
                  </Pie>
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Option Types Bar Chart */}
        {(dashboardData.optionTypes.longCalls.count > 0 || 
          dashboardData.optionTypes.shortCalls.count > 0 || 
          dashboardData.optionTypes.longPuts.count > 0 || 
          dashboardData.optionTypes.shortPuts.count > 0) && (
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h2>Option Positions</h2>
              <Link to="/open-positions" className="card-link">View All →</Link>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <BarChart
                  data={[
                    { 
                      name: 'Long Calls', 
                      Count: dashboardData.optionTypes.longCalls.count,
                      Value: Math.abs(dashboardData.optionTypes.longCalls.value)
                    },
                    { 
                      name: 'Short Calls', 
                      Count: dashboardData.optionTypes.shortCalls.count,
                      Value: Math.abs(dashboardData.optionTypes.shortCalls.value)
                    },
                    { 
                      name: 'Long Puts', 
                      Count: dashboardData.optionTypes.longPuts.count,
                      Value: Math.abs(dashboardData.optionTypes.longPuts.value)
                    },
                    { 
                      name: 'Short Puts', 
                      Count: dashboardData.optionTypes.shortPuts.count,
                      Value: Math.abs(dashboardData.optionTypes.shortPuts.value)
                    }
                  ]}
                  margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
                  <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
                  <Tooltip 
                    formatter={(value, name) => {
                      if (name === 'Count') return value;
                      return formatCurrency(value);
                    }}
                  />
                  <Legend />
                  <Bar yAxisId="left" dataKey="Count" fill="#667eea" name="Count" />
                  <Bar yAxisId="right" dataKey="Value" fill="#764ba2" name="Value (€)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Daily P&L Chart */}
        {dashboardData.dailyPnL.length > 0 && (
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h2>Daily P&L (Last 30 Days)</h2>
              <Link to="/trades" className="card-link">View Trades →</Link>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dashboardData.dailyPnL} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <ReferenceLine y={pnlGoals.daily} stroke="#4caf50" strokeDasharray="5 5" label="Goal" />
                  <Line type="monotone" dataKey="pnl" stroke="#667eea" strokeWidth={2} name="P&L" />
                  <Line type="monotone" dataKey="goal" stroke="#4caf50" strokeDasharray="5 5" name="Goal" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Weekly P&L Chart */}
        {dashboardData.weeklyPnL.length > 0 && (
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h2>Weekly P&L (Last 12 Weeks)</h2>
              <Link to="/trades" className="card-link">View Trades →</Link>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dashboardData.weeklyPnL} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <ReferenceLine y={pnlGoals.weekly} stroke="#4caf50" strokeDasharray="5 5" label="Goal" />
                  <Line type="monotone" dataKey="pnl" stroke="#764ba2" strokeWidth={2} name="P&L" />
                  <Line type="monotone" dataKey="goal" stroke="#4caf50" strokeDasharray="5 5" name="Goal" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Monthly P&L Chart */}
        {dashboardData.monthlyPnL.length > 0 && (
          <div className="dashboard-card chart-card">
            <div className="card-header">
              <h2>Monthly P&L (Last 12 Months)</h2>
              <Link to="/trades" className="card-link">View Trades →</Link>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={dashboardData.monthlyPnL} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip formatter={(value) => formatCurrency(value)} />
                  <Legend />
                  <ReferenceLine y={pnlGoals.monthly} stroke="#4caf50" strokeDasharray="5 5" label="Goal" />
                  <Line type="monotone" dataKey="pnl" stroke="#f44336" strokeWidth={2} name="P&L" />
                  <Line type="monotone" dataKey="goal" stroke="#4caf50" strokeDasharray="5 5" name="Goal" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      <div className="dashboard-actions">
        <button onClick={fetchDashboardData} className="refresh-btn">Refresh Data</button>
      </div>
    </div>
  );
}

export default Home;

